import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_LATEST_VERSIONS_FOR_CSN, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";

import { v4 as uuidv4 } from 'uuid';
import { callLLMDashboardStreamable } from "../../../../amplify/api/llm-chat";
import { getWSService } from "../../../../amplify/api/llm-web-socket";
import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/api-gateway/dashboard-llm";
import { callWrite, EXPLAIN_PALLIATIVE, EXPLAIN_SCORES, EXPLAIN_SX_RX, SCAN_MULTI, SCORES_FILE_TYPE, UPDATE_LAST_VIEWED } from "../../../../amplify/api-gateway/write";
import { useDashboardTrigger } from "../../../../context/DashboardTriggerContext";
import useSetPagePatientVisit from "../../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../cache";
import { AdditionalContextModal } from "../common/AdditionalContextModal";
import { sleep } from "../common/utils";
import { SectionPalliative } from "./SectionPalliative";
import { SectionScores } from "./SectionScores";
import { SectionSxRx } from "./SectionSxRx";
export const JOB_CONFIG = [
    {
        key: "palliative",
        file: EXPLAIN_PALLIATIVE,
    },
    {
        key: "sx_rx",
        file: EXPLAIN_SX_RX,
    },
    {
        key: "scores",
        file: EXPLAIN_SCORES,
        fileType: SCORES_FILE_TYPE,
    },
];

let socketConnection: any;
let sessionId: string;

export const ManagementDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    let { internalCSN } = useParams();
    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);
    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    // Map of explain file_type -> scan_multi payload aligned to that explain's source_version_id
    const [scanMultiByExplain, setScanMultiByExplain] = useState<Record<string, any>>({});

    //
    // State variables for the scan multi popup
    //
    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const latestFileVersionsRef = useRef<Record<string, PatientFileVersion> | undefined>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedInitialSection, setSelectedInitialSection] = useState<string>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    //
    // State variables for the pre-computed data
    //

    const [palliative, setPalliative] = useState<any>();
    const [sxRx, setSxRx] = useState<any>();
    const [scores, setScores] = useState<any>();
    const scanMultiVersionLoadedRef = useRef<Record<string, string | undefined>>({});

    //
    // State variables that hold which sub-section is currently active
    //
    const [palliativeActive, setPalliativeActive] = useState<number>(0);
    const [sxRxActive, setSxRxActive] = useState<number>(0);

    // 
    // Preparing environment
    // 
    const regenerateKeyRef = useRef<any>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();

    const [isLoadingPalliative, setIsLoadingPalliative] = useState<boolean>(false);
    const [isLoadingSxRx, setIsLoadingSxRx] = useState<boolean>(false);
    const [isLoadingScores, setIsLoadingScores] = useState<boolean>(false);

    const sectionToIsLoading: { [key: string]: boolean } = {
        palliative: isLoadingPalliative,
        sx_rx: isLoadingSxRx,
        scores: isLoadingScores,
    };

    const sectionToSetIsLoading: { [key: string]: (val: any) => void } = {
        palliative: setIsLoadingPalliative,
        sx_rx: setIsLoadingSxRx,
        scores: setIsLoadingScores,
    };

    const sectionToGetter: { [key: string]: any } = {
        palliative: palliative,
        sx_rx: sxRx,
        scores: scores,
    };

    const sectionToSetter: { [key: string]: (val: any) => void } = {
        palliative: setPalliative,
        sx_rx: setSxRx,
        scores: setScores,
    };

    const { triggerAction, setTriggerAction } = useDashboardTrigger();
    const visibleJobConfig = useMemo(() => JOB_CONFIG, []);

    const preloadScanMultiForExplain = useCallback((fileTypeToVersion?: Record<string, PatientFileVersion>) => {
        const versions = fileTypeToVersion ?? latestFileVersionsRef.current;
        if (!versions) return;
        const explainKeys = [EXPLAIN_SX_RX, EXPLAIN_PALLIATIVE];
        explainKeys.forEach((explainKey) => {
            const explainVersion = versions?.[explainKey];
            const sourceVersionId = explainVersion?.source_version_id;
            const alreadyLoaded = scanMultiVersionLoadedRef.current[explainKey];
            if (patientVisit && sourceVersionId && alreadyLoaded !== sourceVersionId) {
                dashboardLLMCache.get(SCAN_MULTI, patientVisit, props.tenant, sourceVersionId)
                    .then((data: any) => {
                        setScanMultiByExplain((prev) => ({
                            ...prev,
                            [explainKey]: data,
                        }));
                        scanMultiVersionLoadedRef.current[explainKey] = sourceVersionId;
                    })
                    .catch(() => { /* noop */ });
            }
        });
    }, [patientVisit, props.tenant]);

    useEffect(() => {
        scanMultiVersionLoadedRef.current = {};
    }, [patientVisit?.csn]);

    useEffect(() => {
        latestFileVersionsRef.current = latestFileVersions;
    }, [latestFileVersions]);

    const loadPrecomputedExplanations = useCallback(async (patient: PatientVisit) => {
        const unavailableExplanations: string[] = [];
        for (let section of visibleJobConfig) {
            sectionToSetIsLoading[section.key]?.(true);
            const fileType = section.fileType ?? section.file;
            const cachedCurrentVisit = await dashboardLLMCache.get(fileType, patient, props.tenant);
            if (cachedCurrentVisit) {
                sectionToSetter[section.key]?.(cachedCurrentVisit);
            } else {
                unavailableExplanations.push(section.key);
            }
            sectionToSetIsLoading[section.key]?.(false);
        }
    }, [visibleJobConfig, props.tenant]);

    useEffect(() => {
        if (!patientVisit) return;

        loadPrecomputedExplanations(patientVisit);

        callGetDashboardLLMStatus(patientVisit.csn).then((statuses) => {
            setStatuses(statuses);
        });

        callQuery({
            operation: LLM_VISIT_FEEDBACK_BY_CSN,
            csn: patientVisit.csn,
        }).then(feedbacks => {
            setFeedbacks(feedbacks);
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: patientVisit.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            if (!Array.isArray(latestVersions)) {
                latestVersions = [];
            }
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
            preloadScanMultiForExplain(fileTypeToVersion);
        });
    }, [patientVisit, loadPrecomputedExplanations]);

    useEffect(() => {
        if (!patientVisit) return;
        if (!props.unreadFileVersionMap) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            props.setUnreadFileVersionMap?.(fileVersionMap);

            callWrite({
                operation: UPDATE_LAST_VIEWED,
                csn: patientVisit.csn,
                jobs: visibleJobConfig.map(config => config.file),
            });
        });
    }, [patientVisit, visibleJobConfig]);

    // Update the ref whenever regenerateTarget changes
    useEffect(() => {
        let config = null;
        for (let i = 0; i < visibleJobConfig.length; i++) {
            if (regenerateTarget && visibleJobConfig[i].file !== regenerateTarget) {
                continue;
            }
            config = visibleJobConfig[i];
        }
        regenerateKeyRef.current = config?.key;
    }, [regenerateTarget, visibleJobConfig]);

    const pollUntilDone = async ({
        job,
        startTime,
        delayMs = 2000,
        maxAttempts = 60,
    }: any) => {
        if (patientVisit) {
            sectionToSetIsLoading[job.key]?.(true);
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const statuses = await callGetDashboardLLMStatus(patientVisit.csn);
                if (statuses[job.file] && statuses[job.file].lock_timestamp === null && statuses[job.file].last_sync_timestamp > startTime) {
                    const jobFile = await dashboardLLMCache.get(job.fileType ?? job.file, patientVisit, props.tenant);
                    if (jobFile) {
                        sectionToSetter[job.key]?.(jobFile);

                        // Job completion cleanup (e.g. to get scan file, etc.)
                        callQuery({
                            operation: GET_LATEST_VERSIONS_FOR_CSN,
                            csn: patientVisit.csn,
                        }).then((latestVersions: PatientFileVersion[]) => {
                            if (!Array.isArray(latestVersions)) {
                                latestVersions = [];
                            }
                            const fileTypeToVersion: any = {};
                            for (let latestVersion of latestVersions) {
                                fileTypeToVersion[latestVersion.file_type] = latestVersion;
                            }
                            setLatestFileVersions(fileTypeToVersion);
                        });
                        callGetDashboardLLMStatus(patientVisit.csn).then((statuses) => {
                            setStatuses(statuses);
                        });
                    }
                    sectionToSetIsLoading[job.key]?.(false);
                    setStatuses(statuses);
                    return;
                }

                await sleep(delayMs);
            }
            sectionToSetIsLoading[job.key]?.(false);
            console.error('Polling timed out');
        }
    };

    // Store accumulated text in a ref so we don't trigger extra re-renders
    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            // End of stream cleanup
            if (props.patientVisit && regenerateKeyRef.current) {
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patientVisit.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    if (!Array.isArray(latestVersions)) {
                        latestVersions = [];
                    }
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                    preloadScanMultiForExplain(fileTypeToVersion);
                });

                const config = JOB_CONFIG.find(cfg => cfg.key === regenerateKeyRef.current);
                const fileType = config?.fileType ?? config?.file;
                if (!fileType) {
                    sectionToSetIsLoading[regenerateKeyRef.current]?.(false);
                } else {
                    dashboardLLMCache.get(fileType, props.patientVisit, props.tenant).then((cachedCurrentVisit) => {
                        sectionToSetter[regenerateKeyRef.current]?.(cachedCurrentVisit);
                        sectionToSetIsLoading[regenerateKeyRef.current](false);
                    });
                }
                callGetDashboardLLMStatus(props.patientVisit.csn).then((statuses) => {
                    setStatuses(statuses);
                });
            }
        }
        // Oct 2025: Disable streaming because time-to-first token is high for Azure OpenAI models
        //           which leads to a degraded/non-existent stream. Re-enable once we move back to 
        //           native OpenAI or Claude models.
        //
        // else if (response && typeof response === "object" && "seq" in response && "delta" in response) {
        //     const { seq, delta } = response;

        //     // Optional: Check for skipped or out-of-order chunks
        //     if (seq !== lastSeqRef.current + 1) {
        //         console.warn(`⚠ Out of order chunk: expected ${lastSeqRef.current + 1} but got ${seq}`);
        //         // Optionally handle gap recovery here
        //     }

        //     // Append the new delta to accumulated text
        //     accumulatedTextRef.current += delta;
        //     lastSeqRef.current = seq;

        //     // Push the updated text into your UI
        //     if (regenerateKeyRef.current) {
        //         const key: any = regenerateKeyRef.current;
        //         sectionToSetIsLoading[key](false);
        //         try {
        //             sectionToSetter[key](JSON.parse(jsonrepair(accumulatedTextRef.current)));
        //         } catch {
        //             console.warn(`Could not parse the following accumulated text: ${accumulatedTextRef.current}`);
        //             sectionToSetter[key]({});
        //         }
        //     }
        // }
        // else if (regenerateKeyRef.current) {
        //     // Fallback: if the backend sends a plain string (legacy mode)
        //     const key: any = regenerateKeyRef.current;
        //     sectionToSetIsLoading[key](false);
        //     sectionToSetter[key](response);
        // }
    }, [regenerateTarget]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    }

    const regenerate = async (shouldSyncData: boolean, target?: string) => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        if (patientVisit) {
            setIsRegenerating(true);

            let config = null;

            for (let i = 0; i < JOB_CONFIG.length; i++) {
                if (target && JOB_CONFIG[i].file !== target) {
                    continue;
                }
                if (JOB_CONFIG[i].file.startsWith("scan_")) {
                    continue;
                }
                config = JOB_CONFIG[i];
            }

            connect();

            if (config) {
                sectionToSetIsLoading[config.key]?.(true);
            }

            if (shouldSyncData) {
                await callDashboardLLM({
                    "operation": SCAN_MULTI,
                    "mrn": patientVisit.mrn,
                    "csn": patientVisit.csn,
                });
            }
            if (config) {
                await callLLMDashboardStreamable(sessionId, {
                    operation: config.file,
                    mrn: patientVisit.mrn,
                    csn: patientVisit.csn,
                    report_date_str: new Date().toISOString().split('.')[0] + 'Z',
                });
            }
            setIsRegenerating(false);
        }
    };

    useEffect(() => {
        if (triggerAction && triggerAction.name === 'management') {
            regenerate(true);
            // Important: Reset the trigger action to avoid re-triggering
            setTriggerAction(null);
        }
    }, [triggerAction, regenerate, setTriggerAction]);

    /**
     * Called when the user wants to see the data snapshot for the LLM section
     */
    const handleShowScanMultiModalForSection = (patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            })
            .then((versions: PatientFileVersion[]) => {
                // Find previous version
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    };

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = (fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{fontSize: '1.25rem'}}>Clinical Management</span>
                </Col>
                <Col className="text-right">
                    {
                        patientVisit && (
                            <>
                                {/* {
                                    isRegenerating ? (
                                        <Badge><FontAwesomeIcon className="notes-metadata" icon={faRefresh} spin /> Regenerating...</Badge>
                                    ) : (
                                        <Button variant="outline-secondary" size="sm" onClick={() => {setRegenerateTarget(undefined); setShowContextMenu(true)}} disabled={isRegenerating}><FontAwesomeIcon icon={faRefresh} /> Regenerate All</Button>
                                    )
                                } */}
                                <AdditionalContextModal regenerate={regenerate} showModal={showContextMenu} setShowModal={setShowContextMenu} patientVisit={patientVisit} target={regenerateTarget} tenant={props.tenant} />
                            </>
                        )
                    }
                </Col>
            </Row>

            <SectionScores isLoading={isLoadingScores} data={scores} patientVisit={patientVisit} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={props.unreadFileVersionMap} />
            <SectionSxRx isLoading={isLoadingSxRx} data={scanMultiByExplain[EXPLAIN_SX_RX] ? sxRx : undefined} sxRxActive={sxRxActive} setSxRxActive={setSxRxActive} patientVisit={patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={props.unreadFileVersionMap} tenant={props.tenant} scoresData={scores} scanMultiForCitations={scanMultiByExplain[EXPLAIN_SX_RX]} />
            <SectionPalliative isLoading={isLoadingPalliative} data={scanMultiByExplain[EXPLAIN_PALLIATIVE] ? palliative : undefined} palliativeActive={palliativeActive} setPalliativeActive={setPalliativeActive} patientVisit={patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={props.unreadFileVersionMap} tenant={props.tenant} scoresData={scores} scanMultiForCitations={scanMultiByExplain[EXPLAIN_PALLIATIVE]} />

        </Container>
    );
};
